import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UserMenuContainerComponent } from './user-menu-container.component';
import { HasAnyPermissionDirective } from '../../shared/directives/has-any-permission.directive';
import { HasAnyRoleDirective } from '../../shared/directives/has-any-role.directive';
import { Subscription } from 'rxjs';

@Component({
  selector: 'jhi-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, UserMenuContainerComponent, HasAnyPermissionDirective, HasAnyRoleDirective],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css']
})
export class NavbarComponent implements OnInit, OnDestroy {
  isNavbarCollapsed: boolean = true;
  innerWidth: number = 0;
  currentURl: string = '';
  QrStaff: string | null = null;
  currentUser: any = null;
  private userSubscription?: Subscription;

  constructor(
    private router: Router,
    private authService: AuthService
  ) {
    this.innerWidth = window.innerWidth;
    this.currentURl = this.router.url;
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.currentURl = event.url;
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.innerWidth = window.innerWidth;
  }

  ngOnInit() {
    this.innerWidth = window.innerWidth;
    // Subscribe to current user for permission checking
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  toggleNavbar() {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
  }

  collapseNavbar() {
    this.isNavbarCollapsed = true;
  }

  checkMap() {
    this.currentURl = window.location.href;
    const checkCurrentUrl = this.currentURl.indexOf('map');
    if (checkCurrentUrl !== -1) {
      return false;
    }
    return true;
  }

  isAuthenticated() {
    return this.authService.isAuthenticated();
  }

  login() {
    this.router.navigate(['/login']);
  }

  routerPage(type: string) {
    this.router.navigate(['/', type]);
  }

  isThamDinhRoute(): boolean {
    // Check cả path và query params
    const url = this.currentURl.split('?')[0]; // Lấy path không có query params
    return url.includes('/quy-trinh-tham-dinh') || url.includes('/list');
  }

  hasThamDinhPermission(): boolean {
    return this.hasPermission('hstd:list_all') || this.hasPermission('hstd:read_own');
  }

  private hasPermission(permission: string): boolean {
    if (!this.currentUser) {
      // Check localStorage for permissions (if stored there)
      const permissionStr = localStorage.getItem('permissions');
      if (permissionStr) {
        try {
          const permissions = JSON.parse(permissionStr);
          if (Array.isArray(permissions)) {
            return permissions.some((perm: any) => {
              if (typeof perm === 'string') {
                return perm === permission || perm.includes(permission);
              }
              return perm?.permission === permission || perm?.permission?.includes(permission);
            });
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      return false;
    }

    // Check permissions array
    if (this.currentUser.permissions && Array.isArray(this.currentUser.permissions)) {
      const hasPermission = this.currentUser.permissions.some((perm: any) => {
        if (typeof perm === 'string') {
          return perm === permission || perm.includes(permission);
        }
        return perm?.permission === permission || perm?.permission?.includes(permission);
      });
      if (hasPermission) return true;
    }

    // Check global_permissions array
    if (this.currentUser.global_permissions && Array.isArray(this.currentUser.global_permissions)) {
      const hasPermission = this.currentUser.global_permissions.some((perm: any) => {
        if (typeof perm === 'string') {
          return perm === permission || perm.includes(permission);
        }
        return perm?.permission === permission || perm?.permission?.includes(permission);
      });
      if (hasPermission) return true;
    }

    return false;
  }

  private hasRole(role: string): boolean {
    // Check localStorage 'role' (array of role strings)
    const roleStr = localStorage.getItem('role');
    if (roleStr) {
      try {
        const roles = JSON.parse(roleStr);
        if (Array.isArray(roles)) {
          if (roles.includes(role)) return true;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Check localStorage 'jhi-cn_role' (array of role objects with role property)
    const cnRoleStr = localStorage.getItem('jhi-cn_role');
    if (cnRoleStr) {
      try {
        const cnRoles = JSON.parse(cnRoleStr);
        if (Array.isArray(cnRoles)) {
          const hasRole = cnRoles.some((roleObj: any) => roleObj?.role === role || roleObj?.role?.includes(role));
          if (hasRole) return true;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Check currentUser from AuthService
    if (this.currentUser) {
      // Check roles array
      if (this.currentUser.roles && Array.isArray(this.currentUser.roles)) {
        const hasRole = this.currentUser.roles.some((r: any) => {
          if (typeof r === 'string') {
            return r === role || r.includes(role);
          }
          const roleValue = r?.role || r?.name;
          return roleValue === role || roleValue?.includes(role);
        });
        if (hasRole) return true;
      }

      // Check global_roles array
      if (this.currentUser.global_roles && Array.isArray(this.currentUser.global_roles)) {
        const hasRole = this.currentUser.global_roles.some((r: any) => {
          if (typeof r === 'string') {
            return r === role || r.includes(role);
          }
          const roleValue = r?.role || r?.name;
          return roleValue === role || roleValue?.includes(role);
        });
        if (hasRole) return true;
      }
    }

    return false;
  }

  getHoSoPhanCongText(): string {
    // Check theo thứ tự ưu tiên
    if (this.hasRole('ROLE_PHAN_CONG')) {
      return 'Phân Công';
    }
    if (this.hasRole('ROLE_CHUYEN_VIEN_TD')) {
      return 'Tiếp nhận';
    }
    if (this.hasRole('ROLE_NVKD') || this.hasRole('ROLE_TIN_DUNG')) {
      return 'Hồ sơ yêu cầu';
    }
    // Default fallback
    return 'Phân công';
  }
}
